/**
 * HUD Button Bar
 *
 * A vertical sidebar on the left edge of the screen that holds all
 * panel-toggle buttons (including Advisor/ARIA). Buttons are uniform
 * width and use horizontal text.
 *
 * When the ARIA panel slides open (from the left), the bar shifts right
 * to stay visible via setLeftSidebarShift().
 */

import { attachTooltip } from './tooltip';

const BTN_WIDTH = 130; // px — uniform width for all buttons
const DEFAULT_BORDER_COLOR = 'rgba(100,150,255,0.45)';

let bar: HTMLDivElement | null = null;
let accentColor: string | null = null;
const registeredButtons: HTMLButtonElement[] = [];

function getOrCreateBar(): HTMLDivElement {
  if (bar) return bar;

  bar = document.createElement('div');
  bar.id = 'hud-button-bar';
  bar.style.cssText = `
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 6px;
    background: rgba(0,6,18,0.75);
    border-right: 1px solid rgba(100,150,255,0.25);
    border-radius: 0 6px 6px 0;
    pointer-events: auto;
    z-index: 25;
    transition: left 0.25s ease;
  `;

  document.body.appendChild(bar);
  return bar;
}

/**
 * Register a button in the HUD left sidebar. Returns the created button
 * element so the caller can update it (e.g., change text on active state).
 * @param insertFirst If true, prepend to the bar so it appears at the top.
 */
export function registerHudButton(
  text: string,
  onClick: () => void,
  tooltip?: string,
  insertFirst = false,
): HTMLButtonElement {
  const container = getOrCreateBar();

  const btn = document.createElement('button');
  btn.textContent = text;
  btn.style.cssText = `
    width: ${BTN_WIDTH}px;
    padding: 7px 10px;
    background: rgba(0,0,0,0.75);
    color: #88ccff;
    border: 1px solid rgba(100,150,255,0.45);
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    text-align: left;
    cursor: pointer;
    pointer-events: auto;
    transition: background 0.15s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
  btn.addEventListener('mouseenter', () => {
    btn.style.background = 'rgba(20,50,110,0.9)';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = 'rgba(0,0,0,0.75)';
  });
  btn.addEventListener('click', onClick);
  if (tooltip) attachTooltip(btn, tooltip);

  // Apply current accent color if set
  if (accentColor) {
    btn.style.borderColor = accentColor;
  }

  registeredButtons.push(btn);

  if (insertFirst && container.firstChild) {
    container.insertBefore(btn, container.firstChild);
  } else {
    container.appendChild(btn);
  }
  return btn;
}

/**
 * Shift the sidebar right (or back) when the ARIA panel slides in/out.
 * @param pixels 0 = normal position, 300 = shifted right past open ARIA panel
 */
export function setLeftSidebarShift(pixels: number): void {
  const b = getOrCreateBar();
  b.style.left = `${pixels}px`;
}

/**
 * Set the accent color for all HUD buttons. Pass null to reset to default.
 * Tints button borders subtly — does not change text for readability.
 */
export function setHudAccentColor(color: string | null): void {
  accentColor = color;
  const borderColor = color ?? DEFAULT_BORDER_COLOR;
  for (const btn of registeredButtons) {
    btn.style.borderColor = borderColor;
  }
  // Also tint the bar's right border
  if (bar) {
    bar.style.borderRightColor = color ?? 'rgba(100,150,255,0.25)';
  }
}
